import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const RouterContext = createContext(null);

export const BrowserRouter = ({ children }) => {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((to) => {
    const target = new URL(to, window.location.href);
    if (target.origin !== window.location.origin) {
      window.location.assign(target.href);
      return;
    }

    window.history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`);
    setPathname(target.pathname);
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const value = useMemo(() => ({ navigate, pathname }), [navigate, pathname]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useLocation = () => {
  const router = useContext(RouterContext);
  if (!router) throw new Error('useLocation must be used inside BrowserRouter');
  return { pathname: router.pathname };
};

export const useParams = () => {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/post\/([^/]+)\/?$/);

  if (!match) return {};
  try {
    return { slug: decodeURIComponent(match[1]) };
  } catch {
    return {};
  }
};

export const Link = forwardRef(({ children, onClick, to, target, ...props }, ref) => {
  const router = useContext(RouterContext);

  const handleClick = (event) => {
    onClick?.(event);
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || target === '_blank'
      || !router
    ) {
      return;
    }

    event.preventDefault();
    router.navigate(to);
  };

  return (
    <a ref={ref} href={to} target={target} onClick={handleClick} {...props}>
      {children}
    </a>
  );
});

